import { JSX, useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
    Box,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { NokProcessingFailure } from '../../../types/entities';
import { DashboardCard } from './DashboardCard';
import { ViewAllLink, dashboardListSx } from './ListWidgetChrome';
import { numeric } from './styles/dashboard-tokens';
import { usePermissions } from 'react-admin';
import { loadUnresolvedFailures, rescoreOne, RescoreResult } from '../../edit/components/NokProcessing/nokProcessingApi';
import { permissionManager } from '../../permissions/manager';
import { miniLabel, resultRouteFor, stageLabel } from '../../edit/components/NokProcessing/failureRoutes';

/**
 * Nok scoring that stopped and has not been picked up again.
 *
 * On the dashboard rather than only on its own list screen because of what these used to look
 * like, which was nothing at all. A crash in the Operator was a line in a log file on the
 * Operator's own disk; the Mystery Shopper or Compliance Sniffer result stayed at 'scoring', and
 * the sweep that would have retried it excludes 'scoring' by design. A customer waited for a
 * score nobody knew was missing.
 *
 * **It does not use NewItemsBadge.** That badge is green and says "N new", which reads as good
 * news arriving. These are runs that failed.
 */
export const NokProcessingFailuresData: () => JSX.Element = (): JSX.Element => {
    // HQ-21. Rescoring costs a full scoring pass per Nok, so it is its own ACL resource and
    // HQ-API refuses it without one. An ungated button is a button that can only fail.
    const { permissions } = usePermissions();
    const canRescore: boolean = permissionManager.isAllowed(permissions, 'HQ_Data::nok_rescore');
    const [rows, setRows] = useState<NokProcessingFailure[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string|null>(null);
    const [busyId, setBusyId] = useState<number|null>(null);
    const [notice, setNotice] = useState<string|null>(null);

    /**
     * One loader for both the first render and the refresh after a rescore.
     *
     * `isLive` is passed in rather than read from a ref because the mount effect has to stop
     * writing state after unmount, while the refresh after a rescore is always live by the time
     * it runs.
     */
    const load = useCallback(async (isLive: () => boolean = (): boolean => true): Promise<void> => {
        try {
            const loaded: NokProcessingFailure[] = await loadUnresolvedFailures(10);
            if (isLive()) {
                setRows(loaded);
                setError(null);
            }
        } catch (caught) {
            if (isLive()) {
                setError(caught instanceof Error ? caught.message : 'Could not load the processing failures.');
            }
        } finally {
            if (isLive()) {
                setLoading(false);
            }
        }
    }, []);

    useEffect((): (() => void) => {
        let live: boolean = true;
        void load((): boolean => live);

        return (): void => { live = false; };
    }, [load]);

    /**
     * Retry one row from here.
     *
     * The answer is shown verbatim rather than assumed. A rescore is queued for the next sweep
     * rather than run on the spot, and saying "done" when it is "queued" is how someone ends up
     * refreshing a result that will not change for another minute.
     */
    const retry = async (failure: NokProcessingFailure): Promise<void> => {
        setBusyId(failure.id);
        setNotice(null);
        const result: RescoreResult = await rescoreOne({ failureId: failure.id });
        setNotice(result.message);
        setBusyId(null);
        if (result.status === 'success') {
            await load();
        }
    };

    return (
        <DashboardCard
            eyebrow="Needs attention"
            title="Nok scoring failures"
            minHeight={ 280 }
            busy={ loading }
            actions={ loading || error !== null ? undefined : <FailureCount total={ rows.length } /> }
        >
            <Box sx={ dashboardListSx }>
                { error !== null && (
                    <Typography variant="body2" color="error">{ error }</Typography>
                ) }
                { notice !== null && (
                    <Typography variant="body2" color="text.secondary" sx={ { mb: 1 } }>{ notice }</Typography>
                ) }
                { error === null && !loading && rows.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        { 'Everything scored.' }
                    </Typography>
                ) }
                { rows.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className="RaDatagrid-headerCell">{ 'Mini' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell">{ 'Result' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell">{ 'What happened' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell">{ 'Tries' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell">{ 'Since' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { rows.map((failure: NokProcessingFailure): JSX.Element => (
                                <TableRow key={ failure.id }>
                                    <TableCell className="RaDatagrid-rowCell">
                                        { miniLabel(failure.mini) }
                                    </TableCell>
                                    <TableCell className="RaDatagrid-rowCell">
                                        <RouterLink to={ resultRouteFor(failure.mini, failure.resultId) }>
                                            { failure.resultId }
                                        </RouterLink>
                                    </TableCell>
                                    <TableCell className="RaDatagrid-rowCell">
                                        { /* The reason can be a whole stack trace, so it goes in
                                             the tooltip and the stage is what the row shows. */ }
                                        <Tooltip title={ failure.reason ?? '' }>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={ failure.status === 'retrying' ? 'info' : 'error' }
                                                label={ failure.status === 'retrying'
                                                    ? 'Retrying'
                                                    : stageLabel(failure.stage) }
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className="RaDatagrid-rowCell">{ failure.attempts }</TableCell>
                                    <TableCell className="RaDatagrid-rowCell">
                                        { new Date(failure.createdAt).toLocaleDateString('en-GB') }
                                    </TableCell>
                                    <TableCell className="RaDatagrid-rowCell">
                                        { canRescore &&
                                            <Button
                                                size="small"
                                                disabled={ busyId === failure.id || failure.status === 'retrying' }
                                                onClick={ (): void => { void retry(failure); } }
                                            >
                                                { busyId === failure.id ? 'Queueing' : 'Rescore' }
                                            </Button>
                                        }
                                    </TableCell>
                                </TableRow>
                            )) }
                        </TableBody>
                    </Table>
                ) }
                <ViewAllLink to="/nok_processing_failures" label="All processing failures" />
            </Box>
        </DashboardCard>
    );
}

type FailureCountProps = {
    total: number,
}

/**
 * The header count, in the error palette.
 *
 * Written here rather than reusing NewItemsBadge because that one is green and says "N new". A
 * row on this card is a customer whose score did not arrive.
 */
const FailureCount: (props: FailureCountProps) => JSX.Element = (
    { total }: FailureCountProps
): JSX.Element => {
    if (total <= 0) {
        return <Typography variant="body2" color="text.disabled">{ 'All clear' }</Typography>;
    }

    return (
        <Box
            sx={ {
                display: 'flex',
                alignItems: 'center',
                px: 1.25,
                py: 0.5,
                borderRadius: 5,
                color: 'error.main',
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
            } }
        >
            { /* `numeric`, not `eyebrow`, and an explicit `inherit`. The eyebrow token sets
                 `color: text.secondary` of its own, which lands on this element and beats the
                 error colour the Box sets for its children to inherit. NewItemsBadge uses
                 `numeric` for the same reason. Verified as rgb(211, 47, 47) on the span with
                 getComputedStyle; the image is not colour-accurate. */ }
            <Typography component="span" sx={ { ...numeric, fontSize: 12, fontWeight: 700, color: 'inherit' } }>
                { total === 1 ? '1 failure' : total + ' failures' }
            </Typography>
        </Box>
    );
}
